import { useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface CarouselImage {
  url: string;
  caption: string;
}

interface ImageCarouselProps {
  title: string;
  images: CarouselImage[];
  isDarkMode?: boolean;
}

export function ImageCarousel({
  title,
  images,
  isDarkMode = false,
}: ImageCarouselProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentImage = images[currentIndex];

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <div
      className={`border rounded-lg mt-4 mb-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between p-4 transition ${
          isDarkMode
            ? "hover:bg-gray-700"
            : "hover:bg-gray-50"
        }`}
      >
        <span
          className={`font-medium text-sm ${
            isDarkMode ? "text-gray-200" : "text-gray-900"
          }`}
        >
          {title}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          } ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
        />
      </button>

      {isOpen && (
        <div
          className={`border-t p-4 space-y-4 ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="space-y-3">
            <img
              src={currentImage.url}
              alt={`Image ${currentIndex + 1} of ${images.length}`}
              className="w-full rounded-lg max-h-80 object-cover cursor-pointer hover:opacity-90 transition-opacity"
              onClick={goToNext}
            />
            <p
              className={`text-xs text-center leading-relaxed h-14 flex items-center justify-center ${
                isDarkMode ? "text-gray-400" : "text-gray-700"
              }`}
            >
              {currentImage.caption}
            </p>
          </div>

          {images.length > 1 && (
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevious}
                className="flex items-center gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {currentIndex + 1} of {images.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNext}
                className="flex items-center gap-2"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
